import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { ApplicationError } from "../../../models/errors/application-error";
import SettingService from "../../../services/organisation/setting-service";
import { ApplicationSettingKey } from "../../../models/organisation/application-setting-key";
import { ApplicationSetting } from "../../../models/organisation/application-setting";

interface WhatsappState {
    integrated: boolean;
    wabaId: string | null
    phoneNumberId: string | null,
    embeddedCode: string | null,
    loading: boolean
}

const initialState: WhatsappState = {
    integrated: false,
    wabaId: null,
    phoneNumberId: null,
    embeddedCode: null,
    loading: false
};

export const integrateWhatsapp = createAsyncThunk<
    void,
    { embeddedCode: string; wabaId: string; phoneNumberId: string },
    { state: RootState }
>(
    "whatsapp/integrate",
    async ({ embeddedCode, wabaId, phoneNumberId }, { rejectWithValue }) => {
        const settingService = new SettingService();
        try {
            await settingService.onboardWhatsapp(wabaId, phoneNumberId, embeddedCode);
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const fetchWhatsappSetting = createAsyncThunk<
    ApplicationSetting | null,
    void,
    { state: RootState }
>("whatsapp/fetchSetting", async (_, { rejectWithValue }) => {
    const settingService = new SettingService();
    try {
        return await settingService.getSetting(ApplicationSettingKey.WhatsAppWbaId);
    } catch (error: any) {
        const apiError = ApplicationError.handleApiError(error, {});
        return rejectWithValue(apiError);
    }
});

const whatsappSlice = createSlice({
    name: "whatsapp",
    initialState,
    reducers: {
        setIntegrated: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.integrated = action.payload;
        },
        setEmbeddedCode: (
            state,
            action: PayloadAction<string>
        ) => {
            state.embeddedCode = action.payload;
        },
        setLoginDetails: (
            state,
            action: PayloadAction<{ wabaId: string, phoneNumberId: string }>
        ) => {
            state.wabaId = action.payload.wabaId;
            state.phoneNumberId = action.payload.phoneNumberId;
        },
    },
    extraReducers(builder) {
        builder.addCase(
            integrateWhatsapp.fulfilled,
            (state) => {
                state.integrated = true;
                state.loading = false;
            }
        );
        builder.addCase(
            integrateWhatsapp.pending,
            (state) => {
                state.loading = true;
            }
        );
        builder.addCase(
            fetchWhatsappSetting.pending,
            (state) => {
                state.loading = true;
            }
        );
        builder.addCase(
            fetchWhatsappSetting.fulfilled,
            (state, action: PayloadAction<ApplicationSetting | null>) => {
                state.integrated = action.payload != null && action.payload.value !== "";
                state.loading = false;
            }
        );
    },
});

export const { setIntegrated, setLoginDetails, setEmbeddedCode } = whatsappSlice.actions;

export default whatsappSlice.reducer;
